import { Box } from '@lifeomic/chroma-react/components/Box';
import { Pill } from '@lifeomic/chroma-react/components/Pill';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import React from 'react';
import { isAiGenerated } from '../ExecutiveSummary/utils';
import { ActionPlanFeedbackControls } from './ActionPlanFeedbackControls';
import { ActionPlanActivity } from './action-plan';

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.pxToRem(8),
    padding: theme.spacing(2),
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
  },
  notes: {
    color: theme.palette.text.hint,
    whiteSpace: 'pre-wrap',
  },
  metadata: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  findings: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  feedback: {
    marginLeft: 'auto',
  },
}));

export const ZoriCoachingActionItem: React.FC<{
  carePlanId: string;
  activity: fhir.CarePlanActivity;
  subjectId: string;
  sourceRunId?: string;
}> = ({ carePlanId, activity, subjectId, sourceRunId }) => {
  const classes = useStyles();
  const action = new ActionPlanActivity(activity);
  const title = action.getTitle();
  const notes = action.getDescription();
  const category = action.getCategory();
  const findings = action.getKeyFindings();

  return (
    <Box
      className={classes.root}
      direction="column"
      gap={2}
      role="article"
      aria-label={title || 'Untitled coaching action'}
    >
      <Text className={classes.title} role="heading" aria-level={3}>
        {title || 'Untitled coaching action'}
      </Text>
      {notes ? <Text className={classes.notes}>{notes}</Text> : null}
      <div className={classes.metadata}>
        {category ? <Pill color="primary">{category}</Pill> : null}
        {isAiGenerated(activity.detail) ? (
          <Pill label="AI" variant="highlight" />
        ) : null}
        {title && sourceRunId && activity.id ? (
          <ActionPlanFeedbackControls
            className={classes.feedback}
            subjectId={subjectId}
            carePlanId={carePlanId}
            activityId={activity.id}
            target="action-item"
            sourceRunId={sourceRunId}
          />
        ) : null}
      </div>
      {findings.length ? (
        <div className={classes.findings}>
          {findings.map((finding, index) => (
            <Pill key={`${finding}-${index}`} color="primary">
              {finding}
            </Pill>
          ))}
        </div>
      ) : null}
    </Box>
  );
};

export default ZoriCoachingActionItem;
